import { createQueryablePromiseFromPromise } from '@play-co/timestep-core/ui';
import {
  openPopupPromise,
  PopupDialogOrigin,
} from 'src/lib/popups/popupOpenClose';
import statePromise from 'src/lib/statePromise';
import { isSceneEntered } from 'src/lib/stateUtils';
import {
  getSceneManager,
  waitForAssets,
  waitForItPromise,
  withLoading,
} from 'src/lib/utils';
import { duoMugAssets, petSpeciesAssets } from 'src/loadingGroups';
import * as Care from 'src/replicant/care';
import { DuoItem } from 'src/replicant/care/care.ruleset';
import * as Pet from 'src/replicant/pet';
import * as Duo from 'src/replicant/duo';
import { DuoFriend } from 'src/replicant/duo/duo.asyncGetters';
import { duration } from 'src/replicant/utils/duration';
import { setLockMenu, setPetCrateOpening } from 'src/state/ui';
import StateObserver from 'src/StateObserver';
import { CareItemController } from '../care/CareItemController';
import { levelUpSequence } from '../level/level.sequence';
import { CareItemEntity, EntityManager } from '../logic/EntityManager';
import { getFriendsPet, getPet } from '../pet';
import { getTutorialController } from '../tutorial/TutorialController';
import {
  endVisitSession,
  getVisitDetails,
  startVisitSession,
} from '../visit/visit.sequence';
import i18n from '../../lib/i18n/i18n';
import { Feature } from '../../replicant/analytics';
import * as Daycare from '../../replicant/daycare';
import { AdultSpecies, adultSpecies } from 'src/replicant/pet/pet.ruleset';
import { captureGenericError, debugBreadcrumb } from 'src/lib/sentry';
import { trackPinchConversion } from 'src/lib/analytics/events';
import { appendToConversationData } from '../conversation/conversation.utils';
import { getMutex } from 'src/replicant/utils/mutex';
import { renamePetSequence } from '../pet/pet.sequence';
import { isBuruFriend } from 'src/replicant/friends/friends.getters';
import { track } from 'src/lib/analytics/utils';

/**
 * Invite visited pet to start a duo.
 * @returns
 */
export async function startDuoInviteSequence() {
  const { visitSession } = StateObserver.getState().user.visit;

  // Sanity check.
  if (!visitSession) {
    captureGenericError(
      'Cannot invite pet to duo',
      new Error('No visit session found'),
    );

    return;
  }

  // Check if user has a pending debut.
  const id = visitSession.friendId;
  const hasPendingDebut =
    StateObserver.replicant.asyncGetters.hasPendingDebut(id);

  // Await for tutorial to complete.
  await getTutorialController().handleEvent('duoInviteSequenceStarted');

  let careItemId = null;
  do {
    careItemId = await openPopupPromise('popupSelectCareItem', {
      type: 'invitation',
      additionalTracking: {
        itemType: 'invitation',
      },
    });
    if (
      careItemId &&
      (await openPopupPromise('popupConfirmCareItem', { careItemId }))?.accepted
    ) {
      break;
    }
  } while (careItemId);

  // No care item selected. Bail.
  if (!careItemId) return;

  // Not a duo care item. Bail.
  if (careItemId !== 'i_vowsCoffee' && careItemId !== 'i_ultimateVowsCoffee') {
    return;
  }

  // If user has a pending debut, bail.
  if (
    await withLoading(() => hasPendingDebut, {
      feature: Feature.DUO._,
      subFeature: Feature.DUO.DEBUT,
    })
  ) {
    await openPopupPromise('popupFeatureDisabled', {
      feature: 'duo',
      reason: 'cantInvite',
    });
    return;
  }

  // Perform invitation animations
  await purchaseDuoInvitation(careItemId);
}

/**
 * Perform invitation animations
 */
export async function purchaseDuoInvitation(careItemId: DuoItem) {
  const { consumed } = await StateObserver.invoke.purchaseCareItem(careItemId);
  if (!consumed) return;

  const pet = getPet();
  const friendsPet = getFriendsPet();

  if (!friendsPet) {
    throw new Error('Cannot purchase duo invitation, friends pet not found');
  }

  const { user } = StateObserver.getState();

  track('DuoInviteSend', {
    friend: isBuruFriend(user, friendsPet.id),
    friendPetID: friendsPet.species,
    item: careItemId,
    receivedInvitations: user.duo.invitations.received.filter(
      ({ playerId: invitePlayerId }) => invitePlayerId === friendsPet.id,
    ).length,
    sentInvitations: user.duo.invitations.sent.filter(
      ({ playerId: invitePlayerId }) => invitePlayerId === friendsPet.id,
    ).length,
    bPointsSpent: Care.ruleset.careItems[careItemId]?.price,
    friendID: friendsPet.id,
  });

  const invitationPromise = createQueryablePromiseFromPromise(
    StateObserver.invoke.sendDuoInvitation({
      playerId: friendsPet.id,
      item: careItemId,
    }),
  );

  pet.position.setWandering(false);
  friendsPet.position.setWandering(false);
  pet.conversation.pause();
  friendsPet.conversation.pause();

  await Promise.all([
    pet.moveTo({
      scene: 'friendsLivingRoom',
      location: 'lm',
    }),
    friendsPet.moveTo({
      scene: 'friendsLivingRoom',
      location: 'rm',
    }),
  ]);

  pet.appearance.setAction(
    careItemId === 'i_ultimateVowsCoffee' ? 'inviteUltimate' : 'inviteNormal',
  );
  friendsPet.appearance.setAction('invited');

  await pet.startConversation('duo_inviter_invite');
  await friendsPet.startConversation('duo_invitee_invited');

  if (!invitationPromise.isReady()) {
    await withLoading(() => invitationPromise, {
      feature: Feature.DUO._,
      subFeature: Feature.DUO.INVITATION,
    });
  }

  const { visitSession } = StateObserver.getState().user.visit;
  if (visitSession) {
    debugBreadcrumb('purchaseDuoInvitation end session');
    await endVisitSession({ friendId: friendsPet.id, sendMessage: false });
  }

  await pet.startConversation('duo_inviter_home');

  pet.conversation.start();
}

/**
 * Handle incoming duo invitation.
 * @returns
 */
export async function startDuoInvitationReceivedSequence(
  origin: PopupDialogOrigin | undefined,
) {
  const { user } = StateObserver.getState();
  const now = StateObserver.now();
  const latestInvitation = Duo.getters.getLastUnseenInvitation(user, now);

  // No unseen invitation. Bail.
  if (!latestInvitation) {
    return;
  }
  const pet = getPet();

  await pet.startConversation('duo_invitee_home');
  await getTutorialController().handleEvent('duoInvitationReceived');

  await openDuoPopup({ origin });
}

/**
 * Open duo invitations popup.
 * @param tab - Tab to open. Defaults to 'received'.
 * @returns
 */

export const duoPopupMutex = getMutex();

export async function openDuoPopup(args: {
  tab?: 'received' | 'sent';
  origin: PopupDialogOrigin | undefined;
}) {
  const { tab = 'received', origin } = args;
  await duoPopupMutex.lock();

  try {
    await StateObserver.invoke.updateDuoLastViewed();

    const { sent, received } = await withLoading(
      () => StateObserver.replicant.asyncGetters.getDuoInvitationsDetails(),
      {
        feature: Feature.DUO._,
        subFeature: Feature.DUO.INVITATION_DETAILS,
      },
    );

    // Clear after fetching to make sure user sees the invitation.
    await StateObserver.invoke.clearExpiredDuoInvitations();

    return openPopupPromise('popupDuoInvitations', {
      sent,
      received,
      selectedTab: tab,
      origin,
    });
  } catch (e) {
    // Popup won't be able to release mutex if it fails to open.
    duoPopupMutex.release();
    // Propagate error.
    throw e;
  }
}

export async function startRespondToDuoSequence(friend: DuoFriend) {
  const isInDaycare = Daycare.getters.isInDaycare(
    StateObserver.getState().user,
    StateObserver.now(),
  );
  if (isInDaycare) {
    await openPopupPromise('popupModalWhite', {
      title: i18n('duo.response.unavailable.daycare.title'),
      message: i18n('duo.response.unavailable.daycare.message'),
      tracking: {
        feature: Feature.DUO.RESPONSE_UNAVAILABLE._,
        $subFeature: Feature.DUO.RESPONSE_UNAVAILABLE.DAYCARE,
      },
    });
    return;
  }

  const response = await openPopupPromise('popupDuoResponse', friend);

  if (!response) return;

  await withLoading(
    () =>
      StateObserver.invoke.sendDuoResponse({
        playerId: friend.id,
        timestamp: friend.invitation.timestamp,
        accepted: response.accepted,
        message: response.message,
      }),
    {
      feature: Feature.DUO._,
      subFeature: Feature.DUO.SEND_RESPONSE,
    },
  );

  await openPopupPromise('popupDuoResponseSuccess', {
    name: friend.profile.name,
    photo: friend.profile.photo,
    accepted: response.accepted,
  });

  await startDuoResponseSequence();
}

/**
 * Handle last duo response. Will trigger debut if accepted.
 * @returns
 */

let responseSequenceRunning = false;

export async function startDuoResponseSequence() {
  // Already running. Bail.
  if (responseSequenceRunning) return false;

  // Set running flag.
  responseSequenceRunning = true;

  try {
    // Pause conversation.
    getPet().conversation.pause();

    // Lock menus
    StateObserver.dispatch(setLockMenu(true));

    const { user } = StateObserver.getState();
    const {
      lastResponse,
      debut,
      invitations: { sent },
    } = user.duo;

    // User already saw invitation. Bail.
    if (!lastResponse && !debut) {
      return false;
    }

    const { getCurrentEvolution } = Pet.getters;

    const evolution = getCurrentEvolution(user);

    if (!evolution) {
      captureGenericError(
        'Cannot process duo response',
        new Error('No current evolution found'),
      );
      return false;
    }

    const { stage, species } = evolution;

    // Something is wrong, cannot process response if pet is not an adult.
    // This may happen if a new pet is spawned while another player is trying to debut with this pet.
    if (stage !== 'adult') {
      // Should just delete lastResponse and bail.
      await StateObserver.invoke.deleteLastDuoResponse();
      return false;
    }

    // Make sure pet assets are loaded.
    await waitForAssets(petSpeciesAssets[species], {
      feature: Feature.DUO._,
      subFeature: Feature.DUO.PET_ASSETS,
    });
    appendToConversationData(species);

    // Figure out if current player is the inviter or invitee.
    const isPlayerInviter = sent.some(
      (x) =>
        x.playerId === lastResponse?.playerId &&
        x.timestamp === lastResponse?.timestamp,
    );

    if (isPlayerInviter) {
      await getPet().startConversation('duo_inviter_notified');
    }

    const [details] = await withLoading(
      () =>
        Promise.all([
          StateObserver.replicant.asyncGetters.getDuoInvitationsDetails(),
          waitForAssets(duoMugAssets, {
            feature: Feature.DUO._,
            subFeature: Feature.DUO.MUG_ASSETS,
          }),
        ]),
      {
        feature: Feature.DUO._,
        subFeature: Feature.DUO.DETAILS,
      },
    );

    const invitations = isPlayerInviter ? details.sent : details.received;
    const friend = invitations.find(
      ({ invitation: { timestamp }, id }) =>
        timestamp === lastResponse?.timestamp && id === lastResponse?.playerId,
    );

    // If user account was deleted
    if (!friend?.hasState) {
      // Should just delete lastResponse and exit
      await StateObserver.invoke.deleteLastDuoResponse();
      return false;
    }

    // Something is wrong, friend's pet is not an adult. This can happen if an invitation was sent while the debut was in progress.
    // The friend shouldn't be able to accept the invitation, but we should still handle this case.
    if (!adultSpecies.includes(friend.pet.species as AdultSpecies)) {
      // Should just delete lastResponse and bail.
      await StateObserver.invoke.deleteLastDuoResponse();
      return false;
    }

    // Visit breakers
    // Since these actions are async they result in a replicant error and state desync
    if (
      (!user.publicProfile && !isBuruFriend(user, friend.id)) ||
      !friend.hasPet
    ) {
      await StateObserver.invoke.deleteLastDuoResponse();
      return false;
    }

    const currentScene = isPlayerInviter ? 'friendsLivingRoom' : 'livingRoom';

    getPet().position.setWandering(false);

    // Fetch details of friend's pet.
    const visitDetails = await withLoading(
      () => getVisitDetails(friend.id).get(),
      {
        feature: Feature.DUO._,
        subFeature: Feature.DUO.VISIT_DETAILS,
      },
    );

    // Something is wrong, could not fetch friend's pet details. Bail.
    if (!visitDetails) {
      return false;
    }

    await startVisitSession({
      details: visitDetails,
      // Flip positions when player is not inviter.
      playerTargetPosition: {
        scene: currentScene,
        location: isPlayerInviter ? 'lm' : 'rm',
      },
      friendTargetPosition: {
        scene: currentScene,
        location: isPlayerInviter ? 'rm' : 'lm',
      },
      // Use invitation's pet data.
      overrides: {
        evolution: {
          species: friend.pet.species,
          stage: 'adult',
        },
        petName: friend.pet.name,
        age: friend.pet.age,
      },
    });

    // Hide home buttons so they don't cover the pets.
    await getSceneManager()?.currentScene?.getHomeButtons()?.hideAnimated();

    const inviter = isPlayerInviter ? getPet() : getFriendsPet();
    inviter?.position.setWandering(false);
    const invitee = isPlayerInviter ? getFriendsPet() : getPet();
    invitee?.position.setWandering(false);

    // Make sure friend's pet cannot be clicked.
    getFriendsPet()?.conversation.pause();

    const type =
      friend.invitation.item === 'i_ultimateVowsCoffee' ? 'Ultimate' : 'Normal';

    inviter?.appearance.setAction(`invite${type}`);
    invitee?.appearance.setAction('invited');

    await inviter?.startConversation('duo_inviter_invite');
    await invitee?.startConversation('duo_invitee_answer');

    if (!lastResponse?.accepted) {
      await invitee?.startConversation('duo_invitee_refuse');

      inviter?.appearance.setAction(`refused${type}`);

      if (friend.invitation.message) {
        await invitee?.startConversation('duo_invitee_message_intro');

        await invitee?.conversation.begin(
          'duo_invitee_message',
          friend.pet.species,
          'normal',
        );
      }

      await inviter?.startConversation('duo_inviter_refused');

      inviter?.position.setWandering(false);
      invitee?.position.setWandering(false);

      debugBreadcrumb('startDuoResponseSequence end session');
      await endVisitSession({ friendId: friend.id, sendMessage: false });

      await StateObserver.invoke.deleteLastDuoResponse();

      return false;
    }

    if (invitee?.species) {
      await invitee?.conversation.begin(
        'duo_invitee_accept',
        invitee.species,
        'happy',
      );
    }

    inviter?.position.setWandering(false);
    invitee?.position.setWandering(false);

    const careItemEntityId = EntityManager.getEntityId(
      'careItem',
      friend.invitation.item,
      friend.id,
    );

    const careItem = new CareItemController(
      getSceneManager(),
      EntityManager.createOrGetEntity(careItemEntityId) as CareItemEntity,
    );

    await careItem.position.moveTo({
      scene: currentScene,
      location: 'mugCenter',
    });

    careItem.appearance.animate();

    inviter?.appearance.setAction(`accepted`);

    invitee?.appearance.setAction(`accepted`);
    invitee?.appearance.setFlipped(true);

    await waitForItPromise(duration({ seconds: 2 }));

    // Don't try to claim reward if already claimed, can happen if sequence was interrupted.
    if (!debut?.rewardClaimed) {
      await openPopupPromise('popupDuoReward', {
        item: friend.invitation.item,
      });

      await StateObserver.invoke.claimDuoDebutReward();

      trackPinchConversion({
        action: 'social',
        feature: Feature.DEBUT._,
        subFeature:
          debut?.inviterId === user.id
            ? Feature.DEBUT.INVITER
            : Feature.DEBUT.INVITEE,
        resource: 'gems',
      });
    }

    await startDebutSequence(friend, careItem);

    await StateObserver.invoke.deleteLastDuoResponse();

    return true;
  } finally {
    // Resume conversation.
    getPet().conversation.start();

    // Restore home buttons.
    await getSceneManager()?.currentScene?.getHomeButtons()?.showAnimated();

    // Unlock menus
    StateObserver.dispatch(setLockMenu(false));

    // Reset flag.
    responseSequenceRunning = false;
  }
}

/**
 * Trigger debut sequence.
 * @param friend - Friend to debut with.
 * @param careItem - Care item to destroy after transition.
 */
async function startDebutSequence(
  friend: DuoFriend,
  careItem?: CareItemController,
) {
  const pet = getPet();
  const friendPetController = getFriendsPet();

  void friendPetController
    ?.moveTo(
      {
        scene: 'debut',
        location: 'right',
      },
      0,
    )
    .then(() => {
      friendPetController?.appearance.setFlipped(true);
      friendPetController?.appearance.setAction('rocketIdle');
    });

  await pet.moveTo({
    scene: 'debut',
    location: 'left',
  });

  await statePromise(() => isSceneEntered('debut'));

  // It's safe to dispose of the mug now. Since it's not visible anymore.
  careItem?.destroy();

  pet.appearance.setAction('rocketIdle');

  const scene = getSceneManager()?.getScene('debut');

  await scene?.launch();

  pet.appearance.setAction('rocketLaunch');
  friendPetController?.appearance.setAction('rocketLaunch');

  await waitForItPromise(duration({ seconds: 2 }));

  pet.hide();
  friendPetController?.hide();

  await waitForItPromise(duration({ seconds: 1 }));

  await Promise.all([
    pet.moveTo({
      scene: 'debut',
      location: 'shardLeft',
    }),
    friendPetController?.moveTo({
      scene: 'debut',
      location: 'shardRight',
    }),
  ]);

  pet.appearance.setAction('shardIdle');
  friendPetController?.appearance.setFlipped(true);
  friendPetController?.appearance.setAction('shardIdle');
  pet.show();
  friendPetController?.show();

  await waitForItPromise(duration({ seconds: 2 }));

  await scene?.spawnBox();

  pet.appearance.setAction('shardBox');
  friendPetController?.appearance.setAction('shardBox');

  waitForItPromise(duration({ seconds: 0.8 })).then(() => {
    pet.hide();
    friendPetController?.hide();
  });

  await scene?.shakeBox();

  await startOpenBoxSequence(friend);
}

/**
 * Open duo debut box.
 */
async function startOpenBoxSequence(friend: DuoFriend) {
  let pet = getPet();

  // Freeze pet
  pet.appearance.setAction(undefined);
  pet.appearance.setSpecies(undefined);
  pet.position.setWandering(false);

  // Hide pet information
  StateObserver.dispatch(setPetCrateOpening(true));

  // Show dialog
  await waitForItPromise(500);
  await openPopupPromise('popupAcceptToS', { showTos: false });

  // Create new pet
  await StateObserver.invoke.debut();

  // Load assets for species
  const { user } = StateObserver.getState();

  const evolution = Pet.getters.getCurrentEvolution(user);

  if (!evolution) {
    throw new Error('Cannot start open box sequence, no current evolution');
  }

  const { species } = evolution;
  await waitForAssets(petSpeciesAssets[species], {
    feature: Feature.DUO._,
    subFeature: Feature.DUO.PET_ASSETS,
  });
  appendToConversationData(species);

  // Set species skin
  pet.appearance.setSpecies(species);

  // Show pet movie clip
  const scene = getSceneManager().getScene('debut');
  await scene?.openBox();
  pet.show();

  // Set pet positions
  await pet.moveTo({
    scene: 'debut',
    location: 'center',
  });

  // Show greetings messages
  await pet.startConversation('initial_greetings');

  // Update name
  await renamePetSequence({ origin: undefined });

  // Show level up popup if needed.
  await levelUpSequence(undefined);

  // Show new pet information
  StateObserver.dispatch(setPetCrateOpening(false));

  // Schedule activity
  await StateObserver.invoke.schedulePetActivity();

  await scene?.hideGodrays();

  // Go back home.
  debugBreadcrumb('startOpenBoxSequence end session');
  await endVisitSession({ friendId: friend.id, sendMessage: false });
}

/**
 * Send reminder to an invited friend.
 * @param friend - Friend to send reminder to.
 * @returns
 */
export async function sendDuoReminder(friend: DuoFriend) {
  const response = await openPopupPromise(
    'popupDuoReminderConfirmation',
    friend,
  );

  if (!response) return;

  await withLoading(
    () =>
      StateObserver.invoke.sendDuoReminder({
        playerId: friend.id,
        timestamp: friend.invitation.timestamp,
      }),
    {
      feature: Feature.DUO._,
      subFeature: Feature.DUO.SEND_REMINDER,
    },
  );

  const { user } = StateObserver.getState();

  track('DuoInviteReminderSend', {
    friend: isBuruFriend(user, friend.id),
    friendPetID: friend.pet.species,
    item: friend.invitation.item,
    receivedInvitations: user.duo.invitations.received.filter(
      ({ playerId: invitePlayerId }) => invitePlayerId === friend.id,
    ).length,
    sentInvitations: user.duo.invitations.sent.filter(
      ({ playerId: invitePlayerId }) => invitePlayerId === friend.id,
    ).length,
    bPointsSpent: Care.ruleset.careItems[friend.invitation.item]?.price,
    friendID: friend.id,
  });

  await openPopupPromise('popupDuoReminderSuccess', {
    name: friend.profile.name,
    photo: friend.profile.photo,
  });
}
